import { useTranslation } from 'react-i18next';

import { v4 as uuidv4 } from 'uuid';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { AddUserRequestBody } from 'src/models/GalenData/AddUserRequestBody.model';
import { CustomPatientProfileFieldEnum } from 'src/models/GalenData/CustomField.model';
import { UserRoleEnum } from 'src/models/GalenData/UserRole.model';
import { I18NextNS } from 'src/models/Language.model';

import useGetCurrentUser from './useGetCurrentUser';
import useHandleHttpRequestError from './useHandleHttpRequestError';

export default function useCreateRemoteOrder() {
  const { session } = useRemoteBoundStore();

  const { practiceId } = useGetCurrentUser();

  const { t } = useTranslation(I18NextNS.COMMON);

  const { showHttpRequestError } = useHandleHttpRequestError();

  const createPatient = async ({
    firstName,
    lastName,
    dob,
    id,
    siteId,
  }: {
    firstName: string;
    lastName: string;
    dob: string;
    id: string;
    siteId: string;
  }): Promise<string> => {
    try {
      const res = await Api.remote.getPatientCustomFieldDefinitions();

      const siteField = res.data.content?.find(
        (definition) => definition.name === CustomPatientProfileFieldEnum.Sites,
      );

      if (!siteField) {
        throw new Error(
          t('common.patientFields.siteRequired', { ns: I18NextNS.COMMON }),
        );
      }

      const customData = [
        {
          field: siteField,
          fieldData: `["${siteId}"]`,
        },
      ];

      const patientUserId = uuidv4();
      const user: AddUserRequestBody = {
        user: {
          userId: patientUserId,
          patientId: id,
          firstName,
          lastName,
          dateOfBirth: dob,
          tenant: { tenantId: import.meta.env.VITE_TENANT_ID },
          roles: [
            {
              defaultRole: true,
              practice: { practiceId: practiceId || '' },
              role: UserRoleEnum.Patient,
            },
          ],
          canViewSensitiveData: false,
          loggableAccount: false,
        },
        customData,
      };

      await Api.remote.create_user(user);
      return patientUserId;
    } catch (error) {
      showHttpRequestError(error);
      throw error;
    }
  };

  const createOrder = async (
    orderId: string,
    patientUserId: string,
    template = session.template,
    siteId?: string,
    siteName?: string,
  ): Promise<string> => {
    try {
      await Api.remote.create_order({
        orderId,
        patientUserId,
        orderType: template,
        siteId,
        siteName,
      });

      return orderId;
    } catch (error) {
      showHttpRequestError(error);
      throw error;
    }
  };

  return {
    createPatient,
    createOrder,
  };
}
